import {
	type Category,
	type ImageRef,
	type UpsertCategoryInput,
	upsertCategoryInputSchema,
	uuidv7,
} from "@luckymenu/core";
import { type FormErrors, zodProblems } from "./form-errors";

/** The category form as both apps edit it. Times are "HH:MM", 24 hour. */
export type CategoryDraft = {
	id: string;
	name: string;
	description: string;
	image: ImageRef | null;
	/** False: hidden from the menu with all its items. */
	isActive: boolean;
	/** Shown only between two times, such as a breakfast menu. */
	timed: boolean;
	availableFrom: string;
	availableTo: string;
};

export const CATEGORY_LIMITS = { name: 60, description: 300 } as const;

/** Times a new time window starts with. */
const DEFAULT_WINDOW = { from: "07:00", to: "11:00" };

export function newCategoryDraft(id: string = uuidv7()): CategoryDraft {
	return {
		id,
		name: "",
		description: "",
		image: null,
		isActive: true,
		timed: false,
		availableFrom: DEFAULT_WINDOW.from,
		availableTo: DEFAULT_WINDOW.to,
	};
}

export function categoryToDraft(category: Category): CategoryDraft {
	return {
		id: category.id,
		name: category.name,
		description: category.description ?? "",
		image: category.image,
		isActive: category.isActive,
		timed: category.availableFrom !== null,
		availableFrom: category.availableFrom ?? DEFAULT_WINDOW.from,
		availableTo: category.availableTo ?? DEFAULT_WINDOW.to,
	};
}

export type CategoryFormResult =
	| { ok: true; input: UpsertCategoryInput }
	| { ok: false; errors: FormErrors };

/** The upsert input, or the problems. The photo is sent only when it changed. */
export function categoryDraftToInput(
	draft: CategoryDraft,
	original: Category | null,
): CategoryFormResult {
	const errors: FormErrors = {};
	const name = draft.name.trim();
	if (name === "") errors.name = "required";
	else if (name.length > CATEGORY_LIMITS.name) errors.name = "tooLong";
	if (draft.description.trim().length > CATEGORY_LIMITS.description) errors.description = "tooLong";
	const time = /^([01]\d|2[0-3]):[0-5]\d$/;
	if (
		draft.timed &&
		(!time.test(draft.availableFrom) ||
			!time.test(draft.availableTo) ||
			draft.availableFrom === draft.availableTo)
	)
		errors.availableTo = "timeRange";
	if (Object.keys(errors).length > 0) return { ok: false, errors };

	const imageChanged = (draft.image?.id ?? null) !== (original?.image?.id ?? null);
	const input: UpsertCategoryInput = {
		id: draft.id,
		name,
		description: draft.description.trim(),
		isActive: draft.isActive,
		availableFrom: draft.timed ? draft.availableFrom : null,
		availableTo: draft.timed ? draft.availableTo : null,
		...(imageChanged ? { imageFileId: draft.image?.id ?? null } : {}),
	};
	const parsed = upsertCategoryInputSchema.safeParse(input);
	if (!parsed.success) return { ok: false, errors: zodProblems(parsed.error) };
	return { ok: true, input };
}
